import React, { useState, useRef, useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { FaPlay, FaPause, FaStop, FaTimes } from 'react-icons/fa';
import { IoMic, IoTrashOutline, IoSparkles } from 'react-icons/io5';
import './AudioComposer.css';

const AudioComposer = ({
  voiceBlob,
  voiceBlobUrl,
  onRecordingComplete,
  onCancel,
  onRemove,
  maxDuration = 30
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [error, setError] = useState(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [playbackDuration, setPlaybackDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const waveformRef = useRef(null);
  const playbackWaveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const recordPluginRef = useRef(null);
  const playbackWavesurferRef = useRef(null);
  const timerRef = useRef(null);

  const isPlaybackMode = voiceBlob && voiceBlobUrl;
  const isRecordingMode = !isPlaybackMode;

  // RECORDING MODE
  useEffect(() => {
    if (!isRecordingMode || !waveformRef.current || wavesurferRef.current) return;

    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: '#d4c4b5',
      progressColor: '#8B7355',
      cursorColor: 'transparent',
      barWidth: 3,
      barGap: 2,
      barRadius: 2,
      height: 40,
      normalize: true,
      interact: false,
    });

    const recordPlugin = wavesurfer.registerPlugin(RecordPlugin.create({
      scrollingWaveform: false,
      continuousWaveform: true,
      continuousWaveformDuration: maxDuration,
      renderRecordedAudio: false,
    }));

    wavesurferRef.current = wavesurfer;
    recordPluginRef.current = recordPlugin;

    recordPlugin.on('record-end', (blob) => {
      setIsRecording(false);

      if (blob && blob.size > 0 && onRecordingComplete) {
        onRecordingComplete(blob);
      }
    });

    const startRecording = async () => {
      try {
        await recordPlugin.startRecording();
        setIsRecording(true);

        let elapsed = 0;
        timerRef.current = setInterval(() => {
          elapsed += 1;
          setRecordingDuration(elapsed);
          if (elapsed >= maxDuration) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            if (recordPluginRef.current?.isRecording()) {
              recordPluginRef.current.stopRecording();
            }
          }
        }, 1000);
      } catch (err) {
        console.error('Failed to start recording:', err);
        setError('Microphone access denied');
        setIsRecording(false);
      }
    };

    startRecording();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Take a snapshot of refs and clear them immediately
      const plugin = recordPluginRef.current;
      const ws = wavesurferRef.current;
      recordPluginRef.current = null;
      wavesurferRef.current = null;

      // Defer cleanup to avoid synchronous AudioContext issues
      // This allows React to complete the render cycle first
      setTimeout(() => {
        if (ws) {
          try { ws.destroy(); } catch (e) {}
        }
      }, 0);
    };
  }, [isRecordingMode, maxDuration, onRecordingComplete]);

  const handleStopRecording = async () => {
    if (!recordPluginRef.current) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const plugin = recordPluginRef.current;
    if (plugin?.isRecording?.()) {
      try {
        await plugin.stopRecording();
      } catch (err) {
        console.error('Stop recording error:', err);
      }
    }
  };

  const handleCancelRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Clear refs immediately - useEffect cleanup will handle actual destruction
    const plugin = recordPluginRef.current;
    const ws = wavesurferRef.current;
    recordPluginRef.current = null;
    wavesurferRef.current = null;

    setIsRecording(false);
    setRecordingDuration(0);

    // Don't do deferred cleanup here - useEffect cleanup handles it
    // This avoids "Cannot close a closed AudioContext" errors

    if (onCancel) onCancel();
  };

  // PLAYBACK MODE
  useEffect(() => {
    if (!isPlaybackMode || !playbackWaveformRef.current || playbackWavesurferRef.current) return;

    setIsLoading(true);

    const wavesurfer = WaveSurfer.create({
      container: playbackWaveformRef.current,
      waveColor: '#c8d6e5',
      progressColor: '#1877f2',
      cursorColor: 'transparent',
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      height: 32,
      normalize: true,
      interact: true,
    });

    playbackWavesurferRef.current = wavesurfer;

    wavesurfer.load(voiceBlobUrl);

    wavesurfer.on('ready', () => {
      setIsLoading(false);
      setPlaybackDuration(Math.round(wavesurfer.getDuration()));
    });

    wavesurfer.on('play', () => setIsPlaying(true));
    wavesurfer.on('pause', () => setIsPlaying(false));
    wavesurfer.on('finish', () => {
      setIsPlaying(false);
      setPlaybackProgress(0);
    });
    wavesurfer.on('timeupdate', (currentTime) => {
      const duration = wavesurfer.getDuration();
      setPlaybackProgress(currentTime / duration);
    });

    return () => {
      try { wavesurfer.destroy(); } catch (e) {}
      playbackWavesurferRef.current = null;
    };
  }, [isPlaybackMode, voiceBlobUrl]);

  const togglePlayback = () => {
    const ws = playbackWavesurferRef.current;
    if (ws) ws.playPause();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const remainingTime = Math.max(0, maxDuration - recordingDuration);

  if (error) {
    return (
      <div className="audio-composer audio-composer-error">
        <div className="audio-composer-error-content">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
        <button className="audio-composer-btn-cancel" onClick={onCancel || handleCancelRecording}>
          <FaTimes /> Dismiss
        </button>
      </div>
    );
  }

  if (isPlaybackMode) {
    return (
      <div className="audio-composer audio-composer-playback">
        <button
          type="button"
          className="audio-composer-play-btn"
          onClick={togglePlayback}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="audio-composer-skeleton-play" />
          ) : isPlaying ? (
            <FaPause size={14} />
          ) : (
            <FaPlay size={14} style={{ marginLeft: 2 }} />
          )}
        </button>

        <div className="audio-composer-waveform-wrapper">
          {isLoading ? (
            <div className="audio-composer-skeleton-waveform">
              {Array.from({ length: 40 }).map((_, i) => (
                <div
                  key={i}
                  className="skeleton-bar"
                  style={{ height: `${20 + Math.sin(i * 0.2) * 25}%`, animationDelay: `${i * 20}ms` }}
                />
              ))}
            </div>
          ) : (
            <div ref={playbackWaveformRef} className="audio-composer-waveform" />
          )}
        </div>

        <div className="audio-composer-time">
          <span className="time-current">{formatTime(Math.round(playbackProgress * playbackDuration))}</span>
          <span className="time-separator">/</span>
          <span className="time-total">{formatTime(playbackDuration)}</span>
        </div>

        <button className="audio-composer-btn-remove" onClick={onRemove}>
          <IoTrashOutline />
        </button>
      </div>
    );
  }

  return (
    <div className="audio-composer audio-composer-recording">
      <div className="audio-composer-recording-header">
        <div className="recording-indicator">
          <span className={`recording-pulse ${isPaused ? 'paused' : ''}`}>
            <IoMic />
          </span>
          <span className="recording-label">{isPaused ? 'Paused' : 'Recording'}</span>
        </div>

        <div className={`recording-timer ${remainingTime <= 5 ? 'warning' : ''}`}>
          {formatTime(recordingDuration)}
        </div>
      </div>

      <div className="audio-composer-waveform-wrapper recording">
        <div ref={waveformRef} className="audio-composer-waveform" />
        {remainingTime <= 5 && isRecording && !isPaused && (
          <div className="recording-time-warning">{remainingTime}s left</div>
        )}
      </div>

      <div className="audio-composer-recording-controls">
        <button className="control-btn control-btn-cancel" onClick={handleCancelRecording}>
          Cancel
        </button>

        <button className="control-btn control-btn-done" onClick={handleStopRecording} disabled={recordingDuration < 1}>
          <FaStop size={12} /> Done
        </button>
      </div>
    </div>
  );
};

export default AudioComposer;